import { useState, useCallback } from "react";
import { X, ArrowLeft, Eye, Users, User, LayoutGrid, List, FileSpreadsheet } from "lucide-react";
import axios from "axios";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { API, formatCurrency, formatDateIST, MODE_COLORS } from "./paymentsConstants";

const MODE_COLS = [
  { key: "cash", label: "Cash" },
  { key: "card", label: "Card" },
  { key: "credit", label: "Credit" },
  { key: "eft", label: "EFT" },
  { key: "upi", label: "UPI" },
  { key: "neft_rtgs", label: "NEFT/RTGS" },
  { key: "wallet", label: "Advance" },
  { key: "payout", label: "Payout" },
  { key: "others", label: "Others" },
];

function CashierSummaryTable({ cashiers, summary, onCashierClick }) {
  return (
    <>
      <div className="overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="font-semibold text-xs">Cashier</TableHead>
              {MODE_COLS.map(c => (
                <TableHead key={c.key} className={`font-semibold text-xs text-right ${c.key === "payout" ? "text-pink-700" : ""}`}>{c.label}</TableHead>
              ))}
              <TableHead className="font-semibold text-xs text-right">Total</TableHead>
              <TableHead className="font-semibold text-xs text-center">Txns</TableHead>
              <TableHead className="font-semibold text-xs text-center">Bills</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cashiers.length === 0 && (
              <TableRow><TableCell colSpan={12} className="text-center py-12 text-slate-400 text-sm">No cashier data for this date</TableCell></TableRow>
            )}
            {cashiers.map((c, idx) => (
              <TableRow key={idx} className="hover:bg-slate-50/80 cursor-pointer group transition-colors" onClick={() => onCashierClick(c.cashierName)} data-testid={`cashier-row-${idx}`}>
                <TableCell className="font-semibold text-slate-800">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-100 to-blue-100 flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-indigo-600" />
                    </div>
                    <span className="text-sm">{c.cashierName || "Unknown"}</span>
                  </div>
                </TableCell>
                {MODE_COLS.map(col => (
                  <TableCell key={col.key} className={`text-right tabular-nums text-sm ${col.key === "payout" ? "text-red-600 font-semibold" : ""}`}>
                    {formatCurrency(col.key === "others" ? Math.max(c[col.key] || 0, 0) : (c[col.key] || 0))}
                  </TableCell>
                ))}
                <TableCell className="text-right tabular-nums text-sm font-bold text-slate-900">{formatCurrency(c.total)}</TableCell>
                <TableCell className="text-center text-sm text-slate-500">{c.txn_count}</TableCell>
                <TableCell className="text-center text-sm text-slate-500">{c.bill_count}</TableCell>
                <TableCell>
                  <Eye className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 transition-colors" />
                </TableCell>
              </TableRow>
            ))}
            {cashiers.length > 0 && (
              <TableRow className="bg-slate-900" data-testid="cashier-summary-total-row">
                <TableCell className="font-bold text-sm text-white">TOTAL</TableCell>
                {MODE_COLS.map(col => (
                  <TableCell key={col.key} className={`text-right tabular-nums text-sm font-semibold ${col.key === "payout" ? "text-red-400" : "text-white"}`}>
                    {formatCurrency(col.key === "others" ? Math.max(summary[col.key] || 0, 0) : (summary[col.key] || 0))}
                  </TableCell>
                ))}
                <TableCell className="text-right tabular-nums font-bold text-base text-white">{formatCurrency(summary.total)}</TableCell>
                <TableCell className="text-center text-sm font-semibold text-white">{summary.txn_count}</TableCell>
                <TableCell className="text-center text-sm font-semibold text-white">{summary.bill_count}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function TransactionTable({ transactions, drillSummary }) {
  return (
    <>
      {drillSummary && (
        <div className="px-6 py-3 bg-slate-900 text-white flex flex-wrap items-center gap-x-5 gap-y-1 text-sm" data-testid="drill-down-summary">
          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mr-1">Summary</span>
          <span className="font-medium"><span className="text-emerald-400">Cash:</span> {formatCurrency(drillSummary.Cash)}</span>
          <span className="font-medium"><span className="text-blue-400">Card:</span> {formatCurrency(drillSummary.Card)}</span>
          <span className="font-medium"><span className="text-indigo-400">Credit:</span> {formatCurrency(drillSummary.Credit)}</span>
          <span className="font-medium"><span className="text-amber-400">EFT:</span> {formatCurrency(drillSummary.EFT)}</span>
          <span className="font-medium"><span className="text-violet-400">UPI:</span> {formatCurrency(drillSummary.UPI)}</span>
          <span className="font-medium"><span className="text-cyan-400">NEFT/RTGS:</span> {formatCurrency(drillSummary.NEFT_RTGS)}</span>
          <span className="font-medium"><span className="text-amber-400">Advance:</span> {formatCurrency(drillSummary.Wallet)}</span>
          <span className="font-medium"><span className="text-red-400">Payout:</span> {formatCurrency(drillSummary.Payout)}</span>
          <span className="font-medium"><span className="text-orange-400">Others:</span> {formatCurrency(drillSummary.Others)}</span>
          <span className="ml-auto font-bold text-base">Total: {formatCurrency(drillSummary.total)}</span>
        </div>
      )}
      <div className="overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="font-semibold text-xs">Time</TableHead>
              <TableHead className="font-semibold text-xs">Order No</TableHead>
              <TableHead className="font-semibold text-xs">Type</TableHead>
              <TableHead className="font-semibold text-xs">Bill No</TableHead>
              <TableHead className="font-semibold text-xs">MRN</TableHead>
              <TableHead className="font-semibold text-xs">Adm. No</TableHead>
              <TableHead className="font-semibold text-xs">Scheme</TableHead>
              <TableHead className="font-semibold text-xs">Cashier</TableHead>
              <TableHead className="font-semibold text-xs">Category</TableHead>
              <TableHead className="font-semibold text-xs">Mode</TableHead>
              <TableHead className="font-semibold text-xs text-right">Amount</TableHead>
              <TableHead className="font-semibold text-xs">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.map((txn, idx) => {
              const isPayout = txn.status === "PAYOUT";
              return (
                <TableRow key={idx} className={`hover:bg-slate-50/80 transition-colors ${isPayout ? "bg-red-50/40" : ""}`} data-testid={`drill-txn-${idx}`}>
                  <TableCell className="font-mono text-xs text-slate-500">{txn.time}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{txn.orderNumber || "-"}</TableCell>
                  <TableCell className="text-xs">{txn.orderType || "-"}</TableCell>
                  <TableCell className="font-mono text-xs text-indigo-600 font-medium">{txn.billNo || "-"}</TableCell>
                  <TableCell className="text-xs">{txn.mrn || "-"}</TableCell>
                  <TableCell className="text-xs">{txn.admissionNo || "-"}</TableCell>
                  <TableCell>
                    {txn.scheme ? <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-semibold bg-teal-50 text-teal-700 border border-teal-100">{txn.scheme}</span> : <span className="text-slate-300">-</span>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{txn.cashierName || "-"}</TableCell>
                  <TableCell>
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${
                      txn.category === "Cash" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" :
                      txn.category === "Card" ? "bg-blue-50 text-blue-700 border border-blue-100" :
                      txn.category === "Credit" ? "bg-indigo-50 text-indigo-700 border border-indigo-100" :
                      txn.category === "UPI" ? "bg-violet-50 text-violet-700 border border-violet-100" :
                      txn.category === "EFT" ? "bg-amber-50 text-amber-700 border border-amber-100" :
                      "bg-red-50 text-red-600 border border-red-100"
                    }`}>{txn.category}</span>
                  </TableCell>
                  <TableCell className="text-[10px] text-slate-400 font-mono">{txn.mode}</TableCell>
                  <TableCell className={`text-right tabular-nums text-sm font-semibold ${isPayout ? "text-red-600" : "text-slate-800"}`}>{formatCurrency(txn.amount)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold ${
                      txn.status === "PAID" ? "bg-emerald-50 text-emerald-700" :
                      txn.status === "PAYOUT" ? "bg-red-50 text-red-700 border border-red-200" :
                      "bg-amber-50 text-amber-700"
                    }`}>{txn.status}</span>
                  </TableCell>
                </TableRow>
              );
            })}
            {transactions.length === 0 && (
              <TableRow><TableCell colSpan={12} className="text-center py-12 text-slate-400 text-sm">No transactions found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

export function CollectionDrillDown({
  drillDate, onClose,
  cashierSummaryData, cashierSummaryLoading,
  drillCashier, drillData, drillSummary, drillLoading,
  onCashierClick, onBackToCashiers,
  // Parent passes filter params for Excel export
  startTime, endTime, cashier, fnFilter,
}) {
  const [drillGridView, setDrillGridView] = useState("summary");
  const [allTxns, setAllTxns] = useState([]);
  const [allTxnsLoading, setAllTxnsLoading] = useState(false);

  const fetchAllTxns = useCallback(async () => {
    if (!drillDate) return;
    setAllTxnsLoading(true);
    try {
      const params = { date_from: drillDate, date_to: drillDate };
      if (startTime) params.start_time = startTime;
      if (endTime) params.end_time = endTime;
      if (cashier) params.cashier = cashier;
      if (fnFilter && fnFilter !== "All") params.function = fnFilter;
      const { data } = await axios.get(`${API}/api/payments/transactions`, { params });
      setAllTxns(data.transactions || []);
    } catch (e) { console.error(e); }
    finally { setAllTxnsLoading(false); }
  }, [drillDate, startTime, endTime, cashier, fnFilter]);

  const handleDrillToggle = (view) => {
    setDrillGridView(view);
    if (view === "details" && allTxns.length === 0) fetchAllTxns();
  };

  const handleDrillExport = async () => {
    try {
      const params = { date_from: drillDate, date_to: drillDate, export_format: "xlsx" };
      if (startTime) params.start_time = startTime;
      if (endTime) params.end_time = endTime;
      if (cashier) params.cashier = cashier;
      if (fnFilter && fnFilter !== "All") params.function = fnFilter;
      params.detail_level = (drillGridView === "details" || !!drillCashier) ? "detailed" : "summary";
      const response = await axios.get(`${API}/api/payments/export`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = url; link.download = `collection_${drillDate}.xlsx`; link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  if (!drillDate) return null;
  const isLevel2 = !!drillCashier;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="drill-down-modal">
      <div className="bg-white rounded-2xl max-w-7xl w-full max-h-[85vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            {isLevel2 && (
              <button onClick={() => { onBackToCashiers(); setDrillGridView("summary"); }} className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors" data-testid="back-to-cashiers">
                <ArrowLeft className="w-4 h-4 text-slate-500" />
              </button>
            )}
            <div>
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                {isLevel2 ? (
                  <><User className="w-4 h-4 text-indigo-500" /> {drillCashier}</>
                ) : (
                  <><Users className="w-4 h-4 text-indigo-500" /> {drillGridView === "details" ? "Transaction Details" : "Cashier Wise Summary"}</>
                )}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {formatDateIST(drillDate)}
                {isLevel2
                  ? ` \u00b7 ${drillData.length} transactions`
                  : drillGridView === "details"
                    ? ` \u00b7 ${allTxns.length} transactions`
                    : ` \u00b7 ${cashierSummaryData?.cashiers?.length || 0} cashiers`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isLevel2 && (
              <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="drill-grid-toggle">
                <button onClick={() => handleDrillToggle("summary")}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-all ${drillGridView === "summary" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  <LayoutGrid className="w-3 h-3" /> Summary
                </button>
                <button onClick={() => handleDrillToggle("details")}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-all ${drillGridView === "details" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  <List className="w-3 h-3" /> Details
                </button>
              </div>
            )}
            <button onClick={handleDrillExport}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors shadow-sm"
              data-testid="drill-export-excel"><FileSpreadsheet className="w-3 h-3" /> Excel</button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="drill-down-close">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        {!isLevel2 ? (
          drillGridView === "summary" ? (
            cashierSummaryLoading ? (
              <div className="p-12 text-center text-slate-400">Loading cashier summary...</div>
            ) : (
              <CashierSummaryTable
                cashiers={cashierSummaryData?.cashiers || []}
                summary={cashierSummaryData?.summary || {}}
                onCashierClick={onCashierClick}
              />
            )
          ) : (
            allTxnsLoading ? (
              <div className="p-12 text-center text-slate-400">Loading transactions...</div>
            ) : (
              <TransactionTable transactions={allTxns} drillSummary={cashierSummaryData?.summary ? {
                Cash: cashierSummaryData.summary.cash, Card: cashierSummaryData.summary.card,
                Credit: cashierSummaryData.summary.credit, EFT: cashierSummaryData.summary.eft,
                UPI: cashierSummaryData.summary.upi, NEFT_RTGS: cashierSummaryData.summary.neft_rtgs,
                Wallet: cashierSummaryData.summary.wallet,
                Payout: cashierSummaryData.summary.payout,
                Others: cashierSummaryData.summary.others, total: cashierSummaryData.summary.total,
              } : null} />
            )
          )
        ) : (
          drillLoading ? (
            <div className="p-12 text-center text-slate-400">Loading transactions...</div>
          ) : (
            <TransactionTable transactions={drillData} drillSummary={drillSummary} />
          )
        )}
      </div>
    </div>
  );
}
